import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { SchemaForm } from "../components/SchemaForm";
import { SignaturePad } from "../components/SignaturePad";
import { Badge } from "../components/Badge";
import { SCHEMAS, WORK_PERMIT_TYPES, GFM_SITES } from "../lib/permitSchemas";
import { allowedSites } from "../lib/sites";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, ArrowLeft, Check, AlertTriangle } from "lucide-react";

const isExpired = (dt) => dt && new Date(dt) < new Date();

export default function IssuePermit() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site_location: "", issue_at: "", expiry_at: "",
    issuer_name: "", issuer_job_title: "", issuer_signature: null,
    pic_name: "", pic_signature: null, notes: "",
  });
  const [types, setTypes] = useState([]);
  const [permitData, setPermitData] = useState({});

  useEffect(() => {
    api.get(`/contractors/${id}`).then((r) => {
      setContractor(r.data);
      setForm((f) => ({ ...f, pic_name: r.data.name }));
    }).catch(() => navigate("/"));
  }, [id]);

  useEffect(() => {
    if (user) setForm((f) => ({
      ...f,
      issuer_name: f.issuer_name || user.name || "",
      issuer_job_title: f.issuer_job_title || (user.role === "admin" ? "Administrator" : "Site User"),
    }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) => setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const setField = (type, key, value) => setPermitData((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), [key]: value } }));

  const preview = useMemo(() => ({
    contractor_id: contractor?.id,
    contractor_name: contractor?.name, company: contractor?.company, id_number: contractor?.dbs_number,
    contractor_photo: contractor?.contractor_photo, permit_number: "WP-PREVIEW",
    permit_types: types, issue_at: form.issue_at, expiry_at: form.expiry_at,
  }), [contractor, types, form.issue_at, form.expiry_at]);

  const submit = async () => {
    if (!form.site_location) return toast.error("Please enter the site / location.");
    if (!inductionOkForSite) return toast.error(`No in-date site induction for ${form.site_location}. Add or renew the induction for that site before issuing a permit.`);
    if (!form.issue_at || !form.expiry_at) return toast.error("Please set issue and expiry date/time.");
    if (types.length === 0) return toast.error("Select at least one permit type.");
    if (!form.issuer_name) return toast.error("Enter the Authorised Person name.");
    setSaving(true);
    try {
      const payload = {
        contractor_id: id,
        contractor_name: contractor.name,
        company: contractor.company,
        id_number: contractor.dbs_number || "",
        contractor_mobile: contractor.mobile || "",
        contractor_photo: contractor.contractor_photo,
        site_location: form.site_location,
        issue_at: new Date(form.issue_at).toISOString(),
        expiry_at: new Date(form.expiry_at).toISOString(),
        issuer_name: form.issuer_name,
        issuer_job_title: form.issuer_job_title,
        issuer_signature: form.issuer_signature,
        pic_name: form.pic_name,
        pic_signature: form.pic_signature,
        permit_types: types,
        permit_data: permitData,
        notes: form.notes,
      };
      const { data } = await api.post("/permits", payload);
      toast.success(`Permit ${data.permit_number} issued`);
      navigate(`/permits/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!contractor)
    return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div></AppShell>;

  const activeSites = (contractor?.inductions || [])
    .filter((i) => !isExpired(i.induction_expiry))
    .map((i) => i.site);
  const inductionOkForSite = form.site_location && activeSites.includes(form.site_location);
  const showWarn = form.site_location && !inductionOkForSite;

  return (
    <AppShell>
      <button onClick={() => navigate(`/contractors/${id}`)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to profile
      </button>
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{contractor.reference}</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Issue permit — {contractor.name}</h1>
        <p className="text-muted-foreground">{contractor.company} · DBS {contractor.dbs_number || "—"}</p>
      </div>

      {showWarn && (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="induction-warning">
          <AlertTriangle className="h-4 w-4" /> This contractor has no in-date site induction for {form.site_location}. Add or renew the induction for that site first.
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <Section title="Permit types">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {WORK_PERMIT_TYPES.map((p) => {
                const on = types.includes(p.type);
                return (
                  <button key={p.type} type="button" data-testid={`type-${p.type}`} onClick={() => toggleType(p.type)}
                    className={`flex items-center justify-between rounded-md border p-3 text-left text-sm font-semibold transition-colors ${on ? "border-brand bg-brand/5" : "border-border hover:bg-secondary"}`}>
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-full" style={{ background: p.color }} /> {p.title}
                    </span>
                    {on && <Check className="h-4 w-4 text-brand" />}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="Permit details">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>GFM Site</Label>
                <Select value={form.site_location} onValueChange={(v) => set("site_location", v)}>
                  <SelectTrigger data-testid="site_location"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>
                    {allowedSites(user).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Issue date & time</Label>
                <Input type="datetime-local" data-testid="issue_at" value={form.issue_at} onChange={(e) => set("issue_at", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Expiry date & time</Label>
                <Input type="datetime-local" data-testid="expiry_at" value={form.expiry_at} onChange={(e) => set("expiry_at", e.target.value)} />
              </div>
            </div>
          </Section>

          {types.map((t) => (
            <Section key={t} title={SCHEMAS[t].title}>
              <SchemaForm schema={SCHEMAS[t]} data={permitData[t] || {}} onChange={(k, v) => setField(t, k, v)} />
            </Section>
          ))}

          <Section title="Authorisation & signatures">
            <div className="grid grid-cols-2 gap-4">
              <FieldText label="Authorised Person name" testid="issuer_name" value={form.issuer_name} onChange={(v) => set("issuer_name", v)} />
              <FieldText label="Job title" testid="issuer_job_title" value={form.issuer_job_title} onChange={(v) => set("issuer_job_title", v)} />
              <FieldText label="Person in Charge name" testid="pic_name" value={form.pic_name} onChange={(v) => set("pic_name", v)} />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SignaturePad label="Authorised Person signature" testid="sig-issuer" value={form.issuer_signature} onChange={(v) => set("issuer_signature", v)} />
              <SignaturePad label="Person in Charge signature" testid="sig-pic" value={form.pic_signature} onChange={(v) => set("pic_signature", v)} />
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </Section>

          <Button className="w-full bg-brand hover:bg-brand-dark" size="lg" onClick={submit} disabled={saving || !inductionOkForSite} data-testid="submit-permit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {inductionOkForSite ? "Issue permit" : "Induction required to issue"}
          </Button>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-24">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Badge preview</div>
            <Badge permit={preview} />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-border bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-heading text-lg font-bold tracking-tight text-foreground">{title}</h3>
      {children}
    </div>
  );
}
function FieldText({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
